import { z } from "zod";
import { EventStatus } from "../schemas/EventSchemas.ts";
import { type TypeOfObjectValues } from "../util/index.ts";
import { AssetStatus, AssetType, type AssetTypeType } from "./AssetSchemas.ts";
import { AttributeDataType, ResourceLookup } from "./AttributeSchemas.ts";
import { FormSubmissionStatus } from "./FormSubmissionSchemas.shared.ts";
import { ReportInputValue } from "./ReportSchemas.ts";
import { TimeStateManagementInputsSchema } from "./StateManagementSchemas.ts";

// NOTE: Declared here instead of WorkflowTemplateSchemas.ts to not cause circular dependency
export const WORKFLOW_VARNAME_REGEX = /^\$[A-Z]+[A-Z_0-9]+/;
export const WORKFLOW_VAR_NAME_FORMAT =
  "variable name must start with $ and use only uppercase letters, digits and the dash character";

// General definitions:

// Two main categories of step types:
export const WorkflowStepType = {
  SYSTEM_ACTION: "system_action",
  USER_TASK: "user_task",
  PARALLEL: "parallel",
} as const;

export const WorkflowVisualizationMetadataSchema = z.object({
  x: z.number(),
  y: z.number(),
});

export type WorkflowVisualizationMetadata = z.infer<
  typeof WorkflowVisualizationMetadataSchema
>;

export type WorkflowStepTypeType = TypeOfObjectValues<typeof WorkflowStepType>;

// Optional input and output values for each task in the workflow:
export const WorkflowBaseTaskSchema = z.object({
  output: z.record(z.string(), z.string()).optional(),
  input: z.record(z.string(), z.string()).optional(),
});
export type WorkflowBaseTask = z.infer<typeof WorkflowBaseTaskSchema>;

// Optional input and output values for each task in the workflow:
export const WorkflowPluginConfigSchema = z.object({
  plugin_name: z.string().optional(),
  plugin_version: z.number().int().optional(),
});
export type WorkflowPluginConfig = z.infer<typeof WorkflowPluginConfigSchema>;

///////////////////////////////////////////////////////////////
//////////////////     System Actions     /////////////////////
///////////////////////////////////////////////////////////////

// General definitions:
export const SystemActionType = {
  CREATE_EVENT: "create_event",
  FORM_CHOICE: "form_choice",
  VARIABLE_CHOICE: "variable_choice",
  DELAY: "delay",
  CREATE_FORM: "create_form",
  LOCK_RECORD: "lock_record",
  UNLOCK_RECORD: "unlock_record",
  LOCK_REPORT: "lock_report",
  UNLOCK_REPORT: "unlock_report",
  RUN_CALCULATIONS: "run_calculations",
  RUN_TEMPLATED_REPORT: "run_templated_report",
  RUN_DEFAULT_RECORD_VALUE_CONFIGURATION:
    "run_default_record_value_configuration",
  ASSESS_TEMPLATED_REPORT_FOR_COMMENTS: "assess_templated_report_for_comments",
  UPDATE_CUSTOM_ATTRIBUTES: "update_custom_attributes",
  UPDATE_ATTRIBUTES: "update_attributes",
  SEND_REPORT: "send_report",
  SEND_NOTIFICATION: "send_notification",
  CTR_CONSENSUS_CHOICE: "ctr_consensus_choice",
  RUN_WORKER: "run_worker",
  GENERIC_SYSTEM_ACTION: "generic_system_action",
} as const;

export type SystemActionTypeType = TypeOfObjectValues<typeof SystemActionType>;

export const WorkflowTriggerTimePeriod = {
  HOURS: "hours",
  DAYS: "days",
  WEEKS: "weeks",
  MONTHS: "months",
} as const;

export const WorkflowTriggerTimePeriodSchema = z.enum([
  WorkflowTriggerTimePeriod.HOURS,
  WorkflowTriggerTimePeriod.DAYS,
  WorkflowTriggerTimePeriod.MONTHS,
  WorkflowTriggerTimePeriod.WEEKS,
]);

// Create event action:
export const CreateEventActionSchema = WorkflowBaseTaskSchema.extend({
  type: z.literal(SystemActionType.CREATE_EVENT),
  event_schema_id: z.string().uuid(),
  event_status: z.nativeEnum(EventStatus),
});
export type CreateEventAction = z.infer<typeof CreateEventActionSchema>;

// Form choice action:
export const FormChoiceActionSchema = WorkflowBaseTaskSchema.extend({
  type: z.literal(SystemActionType.FORM_CHOICE),
  form_schema_id: z.string().uuid(),
  choices: z.array(
    z.object({
      answer_filter: z.record(z.string(), z.any()),
      next: z.string().optional(),
      end: z.literal(true).optional(),
    })
  ),
});
export type FormChoiceAction = z.infer<typeof FormChoiceActionSchema>;

// Variable choice action:
export const VariableChoiceActionSchema = WorkflowBaseTaskSchema.extend({
  type: z.literal(SystemActionType.VARIABLE_CHOICE),
  choices: z.array(
    z.object({
      condition: z.record(z.string(), z.any()),
      next: z.string().optional(),
      end: z.literal(true).optional(),
    })
  ),
}).merge(WorkflowPluginConfigSchema);
export type VariableChoiceAction = z.infer<typeof VariableChoiceActionSchema>;

// Delay action:
export const DelayActionSchema = WorkflowBaseTaskSchema.extend({
  type: z.literal(SystemActionType.DELAY),
  time_period: WorkflowTriggerTimePeriodSchema,
  duration: z.number().positive(),
  from: z.enum(["now", "previous_workflow"]).optional(),
});
export type DelayActionType = z.infer<typeof DelayActionSchema>;

// Create form action:
export const CreateFormActionSchema = WorkflowBaseTaskSchema.extend({
  type: z.literal(SystemActionType.CREATE_FORM),
  form_schema_id: z.string().uuid(),
  form_status: z.nativeEnum(FormSubmissionStatus),
}).merge(WorkflowPluginConfigSchema);
export type CreateFormActionType = z.infer<typeof CreateFormActionSchema>;

// Lock record action:
export const LockRecordActionSchema = WorkflowBaseTaskSchema.extend({
  type: z.literal(SystemActionType.LOCK_RECORD),
  record_filter: z.record(z.string(), z.any()),
});
export type LockRecordActionType = z.infer<typeof LockRecordActionSchema>;

// Unlock record action:
export const UnlockRecordActionSchema = WorkflowBaseTaskSchema.extend({
  type: z.literal(SystemActionType.UNLOCK_RECORD),
  record_filter: z.record(z.string(), z.any()),
});
export type UnlockRecordActionType = z.infer<typeof UnlockRecordActionSchema>;

// Lock report action:
export const LockReportActionSchema = WorkflowBaseTaskSchema.extend({
  type: z.literal(SystemActionType.LOCK_REPORT),
  report_id: z.string().uuid().optional(),
  report_filter: z.record(z.string(), z.any()).optional(),
});
export type LockReportAction = z.infer<typeof LockReportActionSchema>;

// Unlock report action:
export const UnlockReportActionSchema = WorkflowBaseTaskSchema.extend({
  type: z.literal(SystemActionType.UNLOCK_REPORT),
  report_id: z.string().uuid(),
  report_filter: z.record(z.string(), z.any()).optional(),
});
export type UnlockReportAction = z.infer<typeof UnlockReportActionSchema>;

// Run calculations action:
export const RunCalculationsActionSchema = WorkflowBaseTaskSchema.extend({
  type: z.literal(SystemActionType.RUN_CALCULATIONS),
  filter: z.record(z.string(), z.any()),
  year_months: z.array(z.string()),
  promote_to_record: z.boolean().optional(),
  fill_year_months: z.boolean().default(false).optional(),
  output: z.record(z.string(), z.any()).optional(),
});

export type RunCalculationsAction = z.infer<typeof RunCalculationsActionSchema>;

// Run DRVC action:
export const RunDRVCActionSchema = WorkflowBaseTaskSchema.extend({
  type: z.literal(SystemActionType.RUN_DEFAULT_RECORD_VALUE_CONFIGURATION),
  filter: z.record(z.string(), z.any()),
  overwrite_all_values: z.boolean(),
  seed_records: z.boolean().optional(),
  fill_year_months: z.boolean().optional(),
});
export type RunDRVCActionType = z.infer<typeof RunDRVCActionSchema>;

// Run templated report action:
export const RunTemplatedReportActionSchema = WorkflowBaseTaskSchema.extend({
  type: z.literal(SystemActionType.RUN_TEMPLATED_REPORT),
  name: z.string(),
  templated_report_name: z.string(),
  report_input: z.record(z.string(), ReportInputValue),
  enable_new_version: z.boolean().optional(),
});

export type RunTemplatedReportAction = z.infer<
  typeof RunTemplatedReportActionSchema
>;

// Assess templated reports for comments:
export const AssessTemplatedReportForCommentsActionSchema =
  WorkflowBaseTaskSchema.extend({
    type: z.literal(SystemActionType.ASSESS_TEMPLATED_REPORT_FOR_COMMENTS),
    report_id: z.string(),
    header_name: z.string(),
    styles_to_assess: z.array(z.string()),
    assessment_success_next: z.string(),
    assessment_failure_next: z.string(),
  });
export type AssessTemplatedReportForCommentsAction = z.infer<
  typeof AssessTemplatedReportForCommentsActionSchema
>;
const AssessTemplatedReportForCommentsActionSchemaEnum =
  AssessTemplatedReportForCommentsActionSchema.keyof().Enum;
export const AssessTemplatedReportForCommentsNextStepProperty = {
  ASSESSMENT_SUCCESS_NEXT:
    AssessTemplatedReportForCommentsActionSchemaEnum.assessment_success_next,
  ASSESSMENT_FAILURE_NEXT:
    AssessTemplatedReportForCommentsActionSchemaEnum.assessment_failure_next,
} as const;
export type AssessTemplatedReportForCommentsNextStepPropertyType =
  (typeof AssessTemplatedReportForCommentsNextStepProperty)[keyof typeof AssessTemplatedReportForCommentsNextStepProperty];

// Send Notification Schema
export const SendNotificationActionSchema = WorkflowBaseTaskSchema.extend({
  type: z.literal(SystemActionType.SEND_NOTIFICATION),
  title: z.string().min(1),
  message: z.string().min(1),
  details: z.array(
    z.object({
      title: z.string().min(1),
      data: z.array(
        z.object({ key: z.string().min(1), value: z.string().min(1) })
      ),
    })
  ),
  users: z.array(z.union([z.string().uuid(), z.string()])).max(10),
  groups: z.array(z.union([z.string().uuid(), z.string()])).max(10),
}).merge(WorkflowPluginConfigSchema);

export type SendNotificationActionType = z.infer<
  typeof SendNotificationActionSchema
>;

// Send Report Schema
export const SendReportActionSchema = SendNotificationActionSchema.extend({
  type: z.literal(SystemActionType.SEND_REPORT),
  report_id: z.union([z.string().uuid(), z.string()]).optional(),
  report_filter: z.record(z.string(), z.any()).optional(),
}).superRefine((obj, ctx) => {
  if (obj.report_id && obj.report_filter) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Cannot provide both report_id and report_filter",
    });
  }
  if (!obj.report_id && !obj.report_filter) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Must provide either report_id or report_filter",
    });
  }
});

export type SendReportActionType = z.infer<typeof SendReportActionSchema>;

export const AttributesToUpdateSchema = z
  .object({
    asset_type: z.nativeEnum(AssetType),
    asset_id: z.string().uuid(),
    custom_attributes: z
      .record(
        z.string(),
        z.union([
          z.string(),
          z.number(),
          z.array(z.string()),
          z.array(z.number()),
        ])
      )
      .refine((obj) => Object.keys(obj).length > 0, {
        message: "Must provide at least one custom attribute",
      })
      .optional(),
    primary_attributes: z
      .object({
        status: z.nativeEnum(AssetStatus).or(z.string()).optional(),
      })
      .optional(),
  })
  .merge(TimeStateManagementInputsSchema)
  .refine((obj) => obj.custom_attributes || obj.primary_attributes, {
    message:
      "Must provide either one of custom_attributes or primary_attributes or both",
  })
  .refine(
    (obj) => {
      if (
        (
          [
            AssetType.FACILITY,
            AssetType.FLOW,
            AssetType.ASSET_GROUP,
          ] as AssetTypeType[]
        ).includes(obj.asset_type) &&
        obj.primary_attributes?.status &&
        !obj.primary_attributes.status.match(WORKFLOW_VARNAME_REGEX)
      ) {
        return (
          obj.primary_attributes.status === AssetStatus.ACTIVE ||
          obj.primary_attributes.status === AssetStatus.INACTIVE
        );
      }
      return true;
    },
    {
      message:
        "For facilities, flows, and asset groups, primary_attributes.status must be either 'active' or 'inactive'",
      path: ["primary_attributes", "status"],
    }
  );

export type AttributesToUpdate = z.infer<typeof AttributesToUpdateSchema>;

export const UpdateCustomAttributesActionSchema = WorkflowBaseTaskSchema.extend(
  {
    type: z.literal(SystemActionType.UPDATE_CUSTOM_ATTRIBUTES),
    assets: z.array(AttributesToUpdateSchema).min(1).max(10),
  }
);
export type UpdateCustomAttributesActionType = z.infer<
  typeof UpdateCustomAttributesActionSchema
>;

export const UpdateAttributesActionSchema = WorkflowBaseTaskSchema.extend({
  type: z.literal(SystemActionType.UPDATE_ATTRIBUTES),
  assets: z.array(AttributesToUpdateSchema).min(1).max(10),
});
export type UpdateAttributesActionType = z.infer<
  typeof UpdateAttributesActionSchema
>;

// Run worker action:
export const WorkerRunInputValuesSchema = z.union([
  z.string(),
  z.number(),
  z.boolean(),
  z.array(z.string()),
  z.null(),
]);
export type WorkerRunInputValues = z.output<typeof WorkerRunInputValuesSchema>;
export const RunWorkerActionSchema = WorkflowBaseTaskSchema.extend({
  type: z.literal(SystemActionType.RUN_WORKER),
  worker_id: z.string().uuid(),
  worker_version_id: z.number().int().positive().optional(),
  worker_run_input: z.record(z.string(), WorkerRunInputValuesSchema).optional(),
});
export type RunWorkerActionType = z.infer<typeof RunWorkerActionSchema>;

// Generic system action:
export const GenericSystemActionInputSchema = z.object({
  path: z.record(z.string(), z.string()).optional(),
  query: z.record(z.string(), z.string()).optional(),
  body: z.record(z.string(), z.any()).optional(),
});
export type GenericSystemActionInput = z.infer<
  typeof GenericSystemActionInputSchema
>;

export const GenericSystemActionSchema = WorkflowBaseTaskSchema.extend({
  type: z.literal(SystemActionType.GENERIC_SYSTEM_ACTION),
  operation: z.string(),
  input: GenericSystemActionInputSchema.optional(),
  output: z.record(z.string(), z.string()).optional(),
});
export type GenericSystemActionType = z.infer<typeof GenericSystemActionSchema>;

///////////////////////////////////////////////////////////////
////////////////////     User Tasks     ///////////////////////
///////////////////////////////////////////////////////////////

export const WorkflowTaskPriorityLabel = {
  HIGHEST: "highest",
  HIGH: "high",
  NONE: "none",
} as const;
export const WorkflowTaskPriorityLabelSchema = z.nativeEnum(
  WorkflowTaskPriorityLabel
);
export type WorkflowTaskPriorityLabelType =
  (typeof WorkflowTaskPriorityLabelSchema)[keyof typeof WorkflowTaskPriorityLabelSchema];

///////////////   Attachments in user tasks   /////////////////
export const WorkflowUserTaskAttachmentType = {
  REPORT: "report",
  FORM_SUBMISSION: "form_submission",
  RECORD: "record",
} as const;

export const BaseWorkflowUserTaskAttachmentSchema = z.object({
  type: z.nativeEnum(WorkflowUserTaskAttachmentType),
  title: z.string(),
});

export const WorkflowUserTaskAttachmentReportSchema =
  BaseWorkflowUserTaskAttachmentSchema.extend({
    type: z.literal(WorkflowUserTaskAttachmentType.REPORT),
    report_id: z.string().min(1),
  });
export type WorkflowUserTaskAttachmentReport = z.infer<
  typeof WorkflowUserTaskAttachmentReportSchema
>;

export const WorkflowUserTaskAttachmentRecordsSchema =
  BaseWorkflowUserTaskAttachmentSchema.extend({
    type: z.literal(WorkflowUserTaskAttachmentType.RECORD),
    asset_type: z.nativeEnum(AssetType),
    filter: z.record(z.string(), z.any()),
  });
export type WorkflowUserTaskAttachmentRecords = z.infer<
  typeof WorkflowUserTaskAttachmentRecordsSchema
>;

export const WorkflowUserTaskAttachmentFormSubmissionSchema =
  BaseWorkflowUserTaskAttachmentSchema.extend({
    type: z.literal(WorkflowUserTaskAttachmentType.FORM_SUBMISSION),
    form_submission_id: z.string().min(1),
  });
export type WorkflowUserTaskAttachmentFormSubmission = z.infer<
  typeof WorkflowUserTaskAttachmentFormSubmissionSchema
>;

export const WorkflowUserTaskAttachmentSchema = z.union([
  WorkflowUserTaskAttachmentReportSchema,
  WorkflowUserTaskAttachmentRecordsSchema,
  WorkflowUserTaskAttachmentFormSubmissionSchema,
]);
export type WorkflowUserTaskAttachment = z.infer<
  typeof WorkflowUserTaskAttachmentSchema
>;
//////////////  End Attachments in user tasks   ///////////////

export const UserTaskType = {
  MANUAL_TASK: "manual_task",
  SUBMIT_FORM: "submit_form",
  COMPLETE_EVENT: "complete_event",
  CHOICE: "choice",
  SUBMIT_DATASET: "submit_dataset",
} as const;

export type UserTaskTypeType = TypeOfObjectValues<typeof UserTaskType>;

export const UserTaskTypeSchema = z.nativeEnum(UserTaskType);

// Every user task can potentially have attachments:
export const WorkflowBaseUserTaskSchema = WorkflowBaseTaskSchema.merge(
  WorkflowPluginConfigSchema
).extend({
  attachments: z
    .array(WorkflowUserTaskAttachmentSchema)
    .min(0)
    .max(3)
    .optional(),
});

// Manual user task:
export const ManualTaskSchema = WorkflowBaseUserTaskSchema.extend({
  type: z.literal(UserTaskType.MANUAL_TASK),
});
export type ManualTask = z.infer<typeof ManualTaskSchema>;

// Form submission user task:
export const FormSubmissionTaskSchema = WorkflowBaseUserTaskSchema.extend({
  type: z.literal(UserTaskType.SUBMIT_FORM),
  form_schema_id: z.string().uuid(),
  autocomplete: z.boolean().optional(),
  auto_attach: z.boolean().optional(),
  form_required: z.boolean().optional(),
});
export type FormSubmissionTaskType = z.infer<typeof FormSubmissionTaskSchema>;

// Complete event user task:
export const CompleteEventTaskSchema = WorkflowBaseUserTaskSchema.extend({
  type: z.literal(UserTaskType.COMPLETE_EVENT),
  event_step_id: z.string().uuid(),
});
export type CompleteEventTask = z.infer<typeof CompleteEventTaskSchema>;

// Choice user task:
export const ChoiceTaskSchema = WorkflowBaseUserTaskSchema.extend({
  type: z.literal(UserTaskType.CHOICE),
  choices: z
    .array(
      z.object({
        id: z.string().max(64).min(1),
        name: z.string().max(256),
        description: z.string().max(1024),
        next: z.string().optional(),
        end: z.literal(true).optional(),
      })
    )
    .min(2, "Must have at least 2 choices")
    .max(10, "Must not have more than 10 choices"),
});
export type ChoiceTask = z.infer<typeof ChoiceTaskSchema>;

export const DatasetSubmissionTaskSchema = WorkflowBaseUserTaskSchema.extend({
  type: z.literal(UserTaskType.SUBMIT_DATASET),
  dataset_id: z.string().uuid(),
  dataset_required: z
    .boolean()
    .optional()
    .describe(
      "Lock task from completion if they do not have a dataset associated"
    ),
});
export type DatasetSubmissionTask = z.infer<typeof DatasetSubmissionTaskSchema>;

/**
 * How much of a date workflow variable to show. Matches attribute date resolution.
 */
export const WorkflowVariableDateResolution = {
  YEAR: "year",
  YEAR_MONTH: "year_month",
  YEAR_MONTH_DAY: "year_month_day",
} as const;
export type WorkflowVariableDateResolutionType =
  (typeof WorkflowVariableDateResolution)[keyof typeof WorkflowVariableDateResolution];

/**
 * Display name and attribute definition for one workflow variable.
 * Variable names are keys of {@link WorkflowVariableMetadataSchema}.
 */
export const WorkflowVariableMetadataEntrySchema = z
  .object({
    display_name: z.string().min(1).max(64),
    data_type: z.nativeEnum(AttributeDataType),
    date_resolution: z.nativeEnum(WorkflowVariableDateResolution).optional(),
    pick_list_values: z.array(z.string()).optional(),
    lookup_entity_type: z.nativeEnum(ResourceLookup).optional(),
    filter: z.record(z.string(), z.unknown()).optional(),
    minimum: z.number().optional(),
    maximum: z.number().optional(),
    min_length: z.number().optional(),
    max_length: z.number().optional(),
    is_long_string: z.boolean().optional(),
    before: z.string().optional(),
    after: z.string().optional(),
  })
  .strict();
export type WorkflowVariableMetadataEntryType = z.infer<
  typeof WorkflowVariableMetadataEntrySchema
>;

/**
 * Catalog of workflow variables that can be shown on the workflow page or a task page.
 */
export const WorkflowVariableMetadataSchema = z.record(
  z.string().regex(WORKFLOW_VARNAME_REGEX, WORKFLOW_VAR_NAME_FORMAT),
  WorkflowVariableMetadataEntrySchema
);
export type WorkflowVariableMetadataType = z.infer<
  typeof WorkflowVariableMetadataSchema
>;

/**
 * Workflow variable names to show, in order. Each name must be a key of {@link WorkflowVariableMetadataSchema}.
 */
export const WorkflowVariablesToDisplaySchema = z.array(
  z.string().regex(WORKFLOW_VARNAME_REGEX, WORKFLOW_VAR_NAME_FORMAT)
);
export type WorkflowVariablesToDisplayType = z.infer<
  typeof WorkflowVariablesToDisplaySchema
>;

export const WorkflowTaskAssigneeSchema = z.object({
  assignee: z.string().uuid().nullish(),
  assignee_user: z.string().uuid().nullish(),
  assignee_group: z.string().uuid().nullish(),
});
export type WorkflowTaskAssignee = z.infer<typeof WorkflowTaskAssigneeSchema>;

export const WorkflowTaskAssignmentFilterSchema = z.record(
  z.string(),
  z.record(z.string(), WorkflowTaskAssigneeSchema)
);

export type WorkflowTaskAssignmentFilter = z.infer<
  typeof WorkflowTaskAssignmentFilterSchema
>;

export const WorkflowTaskDueDateSchema = z.object({
  time_period: WorkflowTriggerTimePeriodSchema,
  duration: z.number().positive(),
});
export type WorkflowTaskDueDate = z.infer<typeof WorkflowTaskDueDateSchema>;

export const WorkflowActionInputSchema = z.object({
  type: z.nativeEnum(WorkflowStepType),
  name: z.string(),
  description: z.string().optional(),
  next: z.string().optional(),
  end: z.boolean().optional(),
  visualization_metadata: WorkflowVisualizationMetadataSchema.optional(),
});
export type WorkflowActionInput = z.infer<typeof WorkflowActionInputSchema>;

export const WorkflowSystemActionSchema = WorkflowActionInputSchema.extend({
  type: z.literal(WorkflowStepType.SYSTEM_ACTION),
  task: z.union([
    CreateEventActionSchema,
    FormChoiceActionSchema,
    VariableChoiceActionSchema,
    DelayActionSchema,
    CreateFormActionSchema,
    LockRecordActionSchema,
    UnlockRecordActionSchema,
    RunDRVCActionSchema,
    RunTemplatedReportActionSchema,
    AssessTemplatedReportForCommentsActionSchema,
    LockReportActionSchema,
    UnlockReportActionSchema,
    RunCalculationsActionSchema,
    UpdateCustomAttributesActionSchema,
    UpdateAttributesActionSchema,
    SendNotificationActionSchema,
    SendReportActionSchema,
    RunWorkerActionSchema,
    GenericSystemActionSchema,
  ]),
});

export type WorkflowSystemActionType = z.infer<
  typeof WorkflowSystemActionSchema
>;

export const WorkflowUserTaskSchema = WorkflowActionInputSchema.extend({
  type: z.literal(WorkflowStepType.USER_TASK),
  task: z.union([
    ManualTaskSchema,
    FormSubmissionTaskSchema,
    CompleteEventTaskSchema,
    ChoiceTaskSchema,
    DatasetSubmissionTaskSchema,
  ]),
  description: z.string().max(1024),
  is_dismissible: z.boolean().optional(),
  priority_id: z.number().int().min(0).max(2),
  assignment_filter: WorkflowTaskAssignmentFilterSchema.nullish(),
  due_date: WorkflowTaskDueDateSchema.optional(),
  restrict_assignee_completion: z.boolean().optional(),
  visualization_metadata: WorkflowVisualizationMetadataSchema.optional(),
  variables_to_display: WorkflowVariablesToDisplaySchema.optional(),
}).merge(WorkflowTaskAssigneeSchema);

export type WorkflowUserTaskType = z.infer<typeof WorkflowUserTaskSchema>;

///////////////////////////////////////////////////////////////
//////////////////     Parallel Branches     /////////////////////
///////////////////////////////////////////////////////////////

export type WorkflowParallelTasksType = {
  type: typeof WorkflowStepType.PARALLEL;
  name: string;
  description?: string;
  next?: string;
  end?: boolean;
  branches: Array<{
    start: string;
    steps: Record<string, WorkflowConfigStepType>;
  }>;
  visualization_metadata?: WorkflowVisualizationMetadata;
};
export type WorkflowConfigStepType =
  | WorkflowSystemActionType
  | WorkflowUserTaskType
  | WorkflowParallelTasksType;

export const WorkflowConfigStepSchema: z.ZodType<WorkflowConfigStepType> =
  z.lazy(() =>
    WorkflowSystemActionSchema.or(WorkflowUserTaskSchema).or(
      z.object({
        type: z.literal(WorkflowStepType.PARALLEL),
        name: z.string().max(256),
        description: z.string().max(1024).optional(),
        next: z.string().optional(),
        end: z.boolean().optional(),
        visualization_metadata: WorkflowVisualizationMetadataSchema.optional(),
        branches: z.array(
          z.object({
            start: z.string(),
            steps: z.record(z.string(), WorkflowConfigStepSchema),
          })
        ),
      })
    )
  );

export const WorkflowConfigSchema = z.object({
  start: z.string(),
  steps: z.record(z.string(), WorkflowConfigStepSchema),
  variable_metadata: WorkflowVariableMetadataSchema.optional(),
  variables_to_display: WorkflowVariablesToDisplaySchema.optional(),
  workflow_name: z.string().optional(),
  workflow_description: z.string().optional(),
});
export type WorkflowConfig = z.infer<typeof WorkflowConfigSchema>;
